import React, { type ReactElement, useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { QueryObserverResult } from '@tanstack/react-query';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import CancelButton from 'atom-components/form-fields-for-hook-form/cancel-button';
import SubmitButton from 'atom-components/form-fields-for-hook-form/submit-button';
import { isMarkFinished } from 'helpers/marking.helpers';
import { pluralise } from 'helpers/string.helpers';
import {
  isAnActiveMarkerOfResponse,
  markersContributingMarksAndMarkItems,
  markersMarkItems,
  markersMarks
} from 'pages/marking/helpers/marker.helpers';
import { markerDisplayName } from 'pages/marking/helpers/presentation.helpers';
import { refreshMarkingTables } from 'pages/marking/helpers/react-query.helpers';
import {
  markersOfResponseParams,
  marksOfResponseParams
} from 'pages/marking/helpers/request-params.helpers';
import type { ChangeMarkerMark } from 'pages/marking/types';
import type {
  BaseCustomerUser,
  PartialOrganisationUserWithPartialUser
} from 'services/assist-api/entities/customer-user/types';
import { useDeleteTestInstanceResponseMarkItems } from 'services/assist-api/entities/test-instance-response-mark-item/hooks';
import {
  useDeleteTestInstanceResponseMarks,
  useTestInstanceResponseMarks
} from 'services/assist-api/entities/test-instance-response-mark/hooks';
import {
  useDeleteTestInstanceResponseMarker,
  usePostTestInstanceResponseMarker,
  useTestInstanceResponseMarkers
} from 'services/assist-api/entities/test-instance-response-marker/hooks';
import type { TestInstanceResponseMarkerWithUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { AssistQueryClient } from 'services/assist-api/query-client';
import ConfirmDeletionOfMarkingWorkModal from './confirm-deletion-of-marking-work-modal';
import Marker from './marker';
import type { ReallocateResponseFormValues } from './reallocate-response-form.schema';
import { reallocateResponseFormSchema } from './reallocate-response-form.schema';

type Props = {
  responseId: number;
  oldMarkerId: number;
  oldOrganisationUser: PartialOrganisationUserWithPartialUser;
  organisationUsers: BaseCustomerUser[];
  onClose: () => void;
  onReallocate: () => void;
};

/** A form used by both the Reallocate Response and Force Reallocate Response modals.
 *
 * If marking work is allocated to the old marker, a confirmation dialog will appear when the form
 * is submitted asking if the admin is sure that they want to delete the old marking work. This
 * confirmation dialog should only ever appear from the Force Reallocate Response modal in practice
 * since the buttons that launch the Reallocate Response modal will launch the Deallocation Results
 * modal instead of the Reallocate Response modal if marking has started.
 */
export default function ReallocateResponseForm({
  responseId,
  oldMarkerId,
  oldOrganisationUser,
  organisationUsers,
  onClose,
  onReallocate
}: Props): ReactElement {
  const [showConfirmDeletionOfMarkingWorkModal, setShowConfirmDeletionOfMarkingWorkModal] =
    useState<boolean>(false);

  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(reallocateResponseFormSchema(oldOrganisationUser.id)),
    defaultValues: {
      organisationUserId: oldOrganisationUser.id
    }
  });
  const { handleSubmit, formState } = methods;
  const { isSubmitting, isSubmitted } = formState;

  const abortMessage = 'Aborting Reallocation.';

  const deleteTestInstanceResponseMarker = useDeleteTestInstanceResponseMarker({
    onSuccess: () => {
      const deallocatedOrganisationUser = organisationUsers.find(
        (ou) => ou.id === oldOrganisationUser.id
      );
      toast.success(
        `${markerDisplayName(deallocatedOrganisationUser)} was deallocated from the response.`
      );
      // Ensure that the marking tasks on the Marker Dashboard are updated straight away.
      // This ensures that any "New Mark" actions that will be added or removed appear straight away
      // based on who was allocated and deallocated from the response.
      // AssistQueryClient.invalidateQueries(['test-instance-response-markers/marking-tasks']);
      refreshMarkingTables();

      // Ensure that when markers and mark items are refetched on the Mark Response form, these
      // queries are up to date and don't cause the form to crash when saving:
      AssistQueryClient.removeQueries(['test-instance-response-mark-items']);
      AssistQueryClient.removeQueries(['test-instance-response-markers']);

      // Reallocation was successful, so we can close the Reallocate Response modal and run further
      // success actions:
      onReallocate();
    },
    onError: (error) => {
      toast.error(`Error deallocating marker from the response: ${error.message}. ${abortMessage}`);
    },
    onSettled: () => {
      onClose();
    }
  });

  const postTestInstanceResponseMarker = usePostTestInstanceResponseMarker({
    onSuccess: ({ data }) => {
      const allocatedOrganisationUser = organisationUsers.find((ou) => ou.id === data.json.user.id);
      toast.success(
        `${markerDisplayName(allocatedOrganisationUser)} was allocated to the response.`
      );
      deleteTestInstanceResponseMarker.mutate(oldMarkerId);
    },
    onError: (error) => {
      toast.error(`Error allocating marker to the response: ${error.message}. ${abortMessage}`);
    }
  });

  const markersQuery = useTestInstanceResponseMarkers<TestInstanceResponseMarkerWithUser>(
    markersOfResponseParams(responseId),
    { enabled: responseId !== undefined }
  );

  const attemptToAllocateNewMarker = useCallback(
    async (newMarkerOrganisationUserId: number) => {
      // Fetch the markers of the response in case further allocations have occurred between opening
      // the modal and saving:
      //
      // Note: we should only clear the markers cache for the particular "markers of response"
      // params here and there may be value in preserving the cache for other variations of this
      // query:
      await AssistQueryClient.invalidateQueries([
        'test-instance-response-markers',
        markersOfResponseParams(responseId)
      ]);
      const latestMarkersQuery = await markersQuery.refetch();
      const markerOfSelectedOrganisationUser = latestMarkersQuery.data?.json.find(
        (mkr) =>
          mkr.user.id === newMarkerOrganisationUserId && mkr.testInstanceResponse.id === responseId
      );
      const selectedOrganisationUser = organisationUsers.find(
        (ou) => ou.id === newMarkerOrganisationUserId
      );
      if (markerOfSelectedOrganisationUser) {
        toast.error(
          `Could not allocate ${markerDisplayName(selectedOrganisationUser)} to the response as ` +
            `they are already allocated. ${abortMessage}`
        );
      } else {
        // Allocate the new marker:
        postTestInstanceResponseMarker.mutate({
          testInstanceResponseId: responseId,
          customerUserId: newMarkerOrganisationUserId
        });
      }
    },
    [markersQuery, organisationUsers, postTestInstanceResponseMarker, responseId]
  );

  // Fetch marks and mark items linked to the response to determine if we can deallocate the old
  // marker.
  const marksQuery = useTestInstanceResponseMarks<ChangeMarkerMark>(
    marksOfResponseParams(responseId),
    // Only run this query when the Submit button is clicked for performance reasons:
    { enabled: isSubmitted }
  );

  const getLatestMarksQuery = useCallback(async (): Promise<
    QueryObserverResult<RequestResponse<ChangeMarkerMark[]>, RequestError>
  > => {
    // Clear the cache to ensure that the marks of the response are refetched from the API if the
    // marker submits the form again:
    await AssistQueryClient.invalidateQueries(['test-instance-response-marks']);

    // Trigger the marks query to run again now that the cache has been cleared:
    return marksQuery.refetch();
  }, [marksQuery]);

  const checkIfOldMarkerHasStartedMarking = useCallback(async (): Promise<boolean> => {
    const latestMarksQuery = await getLatestMarksQuery();
    return isAnActiveMarkerOfResponse(oldMarkerId, latestMarksQuery.data?.json || []);
  }, [getLatestMarksQuery, oldMarkerId]);

  const onSubmit: SubmitHandler<ReallocateResponseFormValues> = useCallback(
    async (formValues): Promise<void> => {
      const oldMarkerHasStartedMarking = await checkIfOldMarkerHasStartedMarking();
      if (oldMarkerHasStartedMarking) {
        setShowConfirmDeletionOfMarkingWorkModal(true);
      } else {
        // Since we're confirmed that the old marker does not have any marking work of the response
        // we can proceed with reallocation:
        attemptToAllocateNewMarker(formValues.organisationUserId || 0);
      }
    },
    [attemptToAllocateNewMarker, checkIfOldMarkerHasStartedMarking]
  );

  const triggerFormSubmission = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const deleteTestInstanceResponseMarks = useDeleteTestInstanceResponseMarks({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(`Failed to delete ${rejected} ${pluralise('mark', rejected)}. ${abortMessage}`);
      }
      if (fulfilled) {
        toast.success(`Deleted ${fulfilled} ${pluralise('mark', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to delete marks: ${error.message}. ${abortMessage}`);
    }
  });

  const deleteTestInstanceResponseMarkItems = useDeleteTestInstanceResponseMarkItems({
    onSuccess: (data: PromiseRejectedResult[]) => {
      const rejected = data.filter((d) => d.status === 'rejected').length;
      const fulfilled = data.length - rejected;
      if (rejected) {
        toast.error(
          `Failed to delete ${rejected} ${pluralise('mark item', rejected)}. ${abortMessage}`
        );
      }
      if (fulfilled) {
        toast.success(`Deleted ${fulfilled} ${pluralise('mark item', fulfilled)}.`);
      }
    },
    onError: (error) => {
      toast.error(`Failed to delete mark items: ${error.message}. ${abortMessage}`);
    }
  });

  const submittingOrSaving =
    isSubmitting ||
    isSubmitted ||
    postTestInstanceResponseMarker.isLoading ||
    deleteTestInstanceResponseMarker.isLoading ||
    deleteTestInstanceResponseMarkItems.isLoading ||
    deleteTestInstanceResponseMarks.isLoading ||
    marksQuery.isLoading;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ConfirmDeletionOfMarkingWorkModal
          isOpen={showConfirmDeletionOfMarkingWorkModal}
          onClose={(): void => {
            setShowConfirmDeletionOfMarkingWorkModal(false);
          }}
          onConfirm={async (): Promise<void> => {
            setShowConfirmDeletionOfMarkingWorkModal(false);

            // Since there may be a delay between when the modal is opened and when it is confirmed,
            // recheck in case any marks have been finished in the interm:
            const latestMarksQuery = await getLatestMarksQuery();

            // Recheck if any contributing marks are finished. This helps avoid a situation where
            // a completed mark contains unscored mark items. This would be a violation of the
            // validation rules of the Mark Response form.
            const someMarkingIsFinished =
              markersContributingMarksAndMarkItems(
                oldMarkerId,
                latestMarksQuery.data?.json || []
              ).filter((mark) => isMarkFinished(mark.status)).length > 0;

            if (someMarkingIsFinished) {
              toast.error(`Some marking of the response is finished. ${abortMessage}`);
            } else {
              // Delete both marks and mark items belonging to the old marker. We will need to
              // explicitly delete the old marker's mark items first since they may belong to marks
              // of other active markers.
              const markItemIdsToDelete = markersMarkItems(
                oldMarkerId,
                latestMarksQuery.data?.json || []
              ).map((markItem) => markItem.id);
              if (markItemIdsToDelete.length > 0) {
                await deleteTestInstanceResponseMarkItems.mutateAsync(markItemIdsToDelete);
              }
              const markIdsToDelete = markersMarks(
                oldMarkerId,
                latestMarksQuery.data?.json || []
              ).map((mark) => mark.id);
              if (markIdsToDelete.length > 0) {
                await deleteTestInstanceResponseMarks.mutateAsync(markIdsToDelete);
              }

              // Now that marks and mark items are deleted, all of the old marker's marking of the
              // response should be deleted, so attempt to submit the form again and it will now
              // pass that check to allow the response to be reallocated:
              triggerFormSubmission();
            }
          }}
          oldMarkerId={oldMarkerId}
          oldOrganisationUser={oldOrganisationUser}
          marksOfResponse={marksQuery.data?.json || []}
        />
        <Marker
          organisationUsers={organisationUsers}
          // Ensures that the list of selectable organisation users is kept up to date each time
          // the user attempts to submit the form. This ensures that we're adding/removing markers
          // to/from the list of selectable markers as this list is changed in other tabs.
          alreadyAllocatedOrganisationUserIds={markersQuery.data?.json.map((m) => m.user.id) || []}
        />
        <FormButtonsContainer>
          <CancelButton onClick={onClose} />
          <SubmitButton disabled={submittingOrSaving}>
            {submittingOrSaving ? 'Attempting Reallocation...' : 'Reallocate Response'}
          </SubmitButton>
        </FormButtonsContainer>
      </form>
    </FormProvider>
  );
}
